import { differenceInDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { TIMELINE_ROW_AXIS_LABEL_WIDTH } from './layout/TimelineRowAxisLabel';

export const DEFAULT_ROW_HEADER_WIDTH = 120;
export const VERTICAL_ROW_HEADER_WIDTH = 32;
export const PARENT_HEADER_HEIGHT = 32;
export const TIMELINE_HEADER_HEIGHT = 70;

export const RESIZE_HANDLE_WIDTH = 4;
export const RESIZE_HANDLE_HITBOX_PADDING = 8;
export const RESIZE_HANDLE_CENTER_OFFSET = 2;

// When a minDayWidth is set (gantt), fitted content fills to just under the
// container width instead of exactly to the edge. Exact fill sits on the
// horizontal-scrollbar threshold, so a resize or rounding difference can toggle
// the scrollbar → change the measured width → refit → flicker loop. A few px of
// slack keeps it stable. Both the day-width and the fit-factor hooks use this so
// the rendered fill and the computed fit floor agree.
const FIT_SCROLLBAR_BUFFER = 2;

/**
 * Inclusive day count for a date range, or 0 when either bound is missing or
 * unparseable.
 *
 * Callers render before the range is known (`from`/`to` arrive as '' on the
 * first pass), and `differenceInDays` yields NaN for those. NaN fails every
 * `<= 0` guard — comparisons against it are always false — so it would flow
 * straight through into the day width and the fit floor, and from there into
 * the zoom ladder, where it collapses every bound and disables the zoom
 * controls. Normalising to 0 here lets the existing guards do their job.
 */
const countDays = (from: string, to: string): number => {
  const days = differenceInDays(new Date(to), new Date(from)) + 1;
  return Number.isFinite(days) ? days : 0;
};

/**
 * Observes the scroll container's width.
 *
 * Callers that need both the day width and the fit floor for the same container
 * must derive them from a single call to this hook (see
 * `useTimelineColumnMetrics`). Observing the same element from two hook
 * instances gives each its own ResizeObserver and its own state, and there is no
 * guarantee both see the final frame of a width animation in the same commit.
 * Whichever one misses it stays stale until the next resize — and because the
 * rendered day width is the product of the two (fitted day width × zoom factor,
 * where the zoom factor is clamped by the fit floor), a disagreement leaves every
 * bar at the wrong width for as long as the layout sits still.
 */
const useObservedContainerWidth = (
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  isExportMode: boolean,
): number => {
  const [containerWidth, setContainerWidth] = useState(() =>
    isExportMode ? window.innerWidth : 0,
  );

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.target.clientWidth);
      }
    });

    resizeObserver.observe(element);
    setContainerWidth(element.clientWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef]);

  return containerWidth;
};

/**
 * The lowest zoom factor at which the timeline columns still fill (or overflow)
 * the container — i.e. zooming out further than this would leave empty space to
 * the right. 1 when the content already fits at 1x (short ranges); below 1 only
 * when the range is long enough that `fittedDayWidth` is pinned to `minDayWidth`
 * and the content overflows. Derived from the observed container width and date
 * range only (never the post-zoom rendered width), so it is stable across zoom
 * changes and cannot feed a zoom/resize loop.
 *
 * `null` until the container has actually been measured, so callers can tell
 * "not known yet" apart from a real floor of 1 and avoid clamping a restored
 * zoom level against a placeholder.
 */
const computeFitZoomFactor = ({
  containerWidth,
  from,
  to,
  rowHeaderWidth,
  minDayWidth,
}: {
  containerWidth: number;
  from: string;
  to: string;
  rowHeaderWidth: number;
  minDayWidth: number;
}): number | null => {
  if (containerWidth <= 0) return null;
  const timelineWidth = containerWidth - rowHeaderWidth - FIT_SCROLLBAR_BUFFER;
  const totalDays = countDays(from, to);
  if (timelineWidth <= 0 || totalDays <= 0) return 1;
  const unflooredFitDayWidth = timelineWidth / totalDays;
  // fittedDayWidth is max(unfloored, minDayWidth). It overflows the container
  // only when pinned to the floor, and then the fit factor is unfloored/floor.
  return Math.min(1, unflooredFitDayWidth / minDayWidth);
};

export const useTimelineFitZoomFactor = ({
  scrollContainerRef,
  from,
  to,
  rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
  minDayWidth,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  from: string;
  to: string;
  rowHeaderWidth?: number;
  minDayWidth: number;
}): number | null => {
  const containerWidth = useObservedContainerWidth(scrollContainerRef, false);

  return useMemo(
    () => computeFitZoomFactor({ containerWidth, from, to, rowHeaderWidth, minDayWidth }),
    [containerWidth, from, to, rowHeaderWidth, minDayWidth],
  );
};

interface TimelineDayWidthInput {
  containerWidth: number;
  hasVerticalSubHeaders: boolean;
  hasRowAxisLabel: boolean;
  from: string;
  to: string;
  rowHeaderWidth: number;
  minDayWidth?: number;
}

const computeDayWidth = ({
  containerWidth,
  hasVerticalSubHeaders,
  hasRowAxisLabel,
  from,
  to,
  rowHeaderWidth,
  minDayWidth,
}: TimelineDayWidthInput): number => {
  const axisLabelWidth = hasRowAxisLabel ? TIMELINE_ROW_AXIS_LABEL_WIDTH : 0;
  const timelineWidth = hasVerticalSubHeaders
    ? containerWidth - rowHeaderWidth - PARENT_HEADER_HEIGHT - axisLabelWidth
    : containerWidth - rowHeaderWidth - axisLabelWidth;
  // See FIT_SCROLLBAR_BUFFER: a few px of slack for the gantt (minDayWidth) case
  // so content fills to just under the edge and can't thrash the scrollbar on
  // resize. The larger buffer for the no-minDayWidth (forced-fit) case is kept.
  const scrollbarBuffer = minDayWidth !== undefined ? FIT_SCROLLBAR_BUFFER : 10;
  const bufferedWidth = timelineWidth - scrollbarBuffer;
  const totalDays = countDays(from, to);
  // No usable range yet (first render before from/to arrive): fall back to the
  // floor when there is one, and to 0 otherwise, rather than dividing by zero.
  if (totalDays <= 0) return minDayWidth ?? 0;
  const fittedWidth =
    totalDays <= 31 ? Math.floor(bufferedWidth / totalDays) : bufferedWidth / totalDays;

  if (minDayWidth !== undefined) {
    return Math.max(fittedWidth, minDayWidth);
  }
  return fittedWidth;
};

export const useTimelineColumnDayWidth = ({
  scrollContainerRef,
  hasVerticalSubHeaders,
  hasRowAxisLabel = true,
  from,
  to,
  rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
  isExportMode = false,
  minDayWidth,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  hasVerticalSubHeaders: boolean;
  hasRowAxisLabel?: boolean;
  from: string;
  to: string;
  rowHeaderWidth?: number;
  isExportMode?: boolean;
  minDayWidth?: number;
}) => {
  const containerWidth = useObservedContainerWidth(scrollContainerRef, isExportMode);

  return useMemo(
    () =>
      computeDayWidth({
        containerWidth,
        hasVerticalSubHeaders,
        hasRowAxisLabel,
        from,
        to,
        rowHeaderWidth,
        minDayWidth,
      }),
    [containerWidth, from, to, hasVerticalSubHeaders, hasRowAxisLabel, rowHeaderWidth, minDayWidth],
  );
};

/**
 * Day width and fit floor for one container, derived from a single width
 * measurement so the two can never disagree.
 *
 * The rendered day width is `dayWidth × zoomFactor`, and `zoomFactor` is clamped
 * by `fitZoomFactor`. Measuring those two inputs separately lets one go stale
 * while the other updates (see `useObservedContainerWidth`), which permanently
 * rescales every bar — and bars near `MIN_WIDTH_TO_SHOW_DATES` gain or lose their
 * date labels as a result. Use this instead of calling the two hooks separately.
 */
export const useTimelineColumnMetrics = ({
  scrollContainerRef,
  hasVerticalSubHeaders,
  hasRowAxisLabel = true,
  from,
  to,
  rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
  isExportMode = false,
  minDayWidth,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  hasVerticalSubHeaders: boolean;
  hasRowAxisLabel?: boolean;
  from: string;
  to: string;
  rowHeaderWidth?: number;
  isExportMode?: boolean;
  minDayWidth: number;
}): { dayWidth: number; fitZoomFactor: number | null } => {
  const containerWidth = useObservedContainerWidth(scrollContainerRef, isExportMode);

  return useMemo(
    () => ({
      dayWidth: computeDayWidth({
        containerWidth,
        hasVerticalSubHeaders,
        hasRowAxisLabel,
        from,
        to,
        rowHeaderWidth,
        minDayWidth,
      }),
      fitZoomFactor: computeFitZoomFactor({
        containerWidth,
        from,
        to,
        rowHeaderWidth,
        minDayWidth,
      }),
    }),
    [containerWidth, from, to, hasVerticalSubHeaders, hasRowAxisLabel, rowHeaderWidth, minDayWidth],
  );
};
